import React, { useMemo } from 'react';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ConfluenceIcon } from '@atlaskit/logo';
import { AppLogo, CustomLogo } from '@atlaskit/navigation-system/top-nav-items';

import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback';
import { WIKI } from '@confluence/named-routes/entry-points/routes';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';

import type {
	TopNavigationQuery_siteConfiguration as SiteConfiguration,
	TopNavigationQuery_siteSettings as SiteSettings,
	TopNavigationQuery_siteDescription as SiteDescription,
} from './__types__/TopNavigationQuery';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks'))
			.fireLogoClickedAnalytics,
);

const customLogo = css({
	width: '28px',
	height: '28px',
});

type SiteLogoButtonProps = {
	siteConfiguration: SiteConfiguration | undefined;
	siteSettings: SiteSettings | undefined | null;
	siteDescription: SiteDescription | undefined | null;
	onLoadCustomLogo?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
};

export const SiteLogoButton = ({
	siteConfiguration,
	siteSettings,
	siteDescription,
	onLoadCustomLogo,
}: SiteLogoButtonProps) => {
	const routeDataRef = useRouteDataRef();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onClick = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef.current?.routeName);
	const siteTitle = siteSettings && siteSettings.siteTitle;
	const isNav4Enabled = useIsNav4Enabled();

	const CustomNavLogo = useMemo(() => {
		if (siteConfiguration?.customSiteLogo && siteDescription?.logoUrl) {
			if (isNav4Enabled) {
				return () => (
					<img alt="" src={siteDescription.logoUrl ?? undefined} onLoad={onLoadCustomLogo} />
				);
			}
			return () => (
				<img
					alt=""
					src={siteDescription.logoUrl ?? undefined}
					css={customLogo}
					onLoad={onLoadCustomLogo}
				/>
			);
		}

		return null;
	}, [
		isNav4Enabled,
		onLoadCustomLogo,
		siteConfiguration?.customSiteLogo,
		siteDescription?.logoUrl,
	]);

	return CustomNavLogo ? (
		<CustomLogo
			label={siteTitle ?? 'Confluence'}
			logo={CustomNavLogo}
			icon={CustomNavLogo}
			onClick={onClick}
			href={WIKI.toUrl()}
		/>
	) : (
		<AppLogo
			label={siteTitle ?? 'Confluence'}
			icon={ConfluenceIcon}
			name="Confluence"
			onClick={onClick}
			href={WIKI.toUrl()}
		/>
	);
};
